import { useCallback, useEffect, useRef, useState } from 'react';
import { usePrefersReducedMotion } from './use-prefers-reduced-motion';

/**
 * The step a scroll offset lands on, and the offset that lands on a step — one mapping, both ways.
 *
 * They are a pair on purpose: the read and the jump have to agree or a click would show step 3 while
 * the page still sits at step 1, and the next turn of the wheel would snap it back. Exported for the
 * unit test that pins them as inverses; the hook below is their only other caller.
 */
export function stepFromOffset(offset: number, travel: number, steps: number) {
	if (travel <= 0 || steps <= 1) return 0;
	const progress = Math.min(1, Math.max(0, offset / travel));
	// `steps - 1` so the last step is reached exactly at the bottom rather than one step early.
	return Math.round(progress * (steps - 1));
}

export function offsetForStep(index: number, travel: number, steps: number) {
	if (steps <= 1) return 0;
	return (Math.min(steps - 1, Math.max(0, index)) / (steps - 1)) * travel;
}

/**
 * How far through a pinned section we are, as a step index.
 *
 * Driven by the section's own rect rather than a library: the sticky child is pinned for exactly the
 * section's scrollable length, so the fraction of that length already scrolled IS the progress. Read
 * inside `requestAnimationFrame` so a fast wheel does not force a layout per event.
 *
 * Used by the NGO "how to collaborate" deck, whose card stack the page scroll walks through.
 *
 * `enabled` is the consumer's cue to give the section its extra height AT ALL: a visitor who asked for
 * less motion gets neither the pin nor the several screens of travel that only exist to feed it. It is
 * deliberately not derived from layout — the height is what creates the travel, so asking whether there
 * is travel before deciding to add any would never start.
 */
export function useScrollStep(steps: number) {
	const ref = useRef<HTMLDivElement>(null);
	const [active, setActive] = useState(0);
	const enabled = !usePrefersReducedMotion();

	/** The section's travel, in the two numbers every read and every jump needs. Null while unpinnable. */
	const geometry = useCallback(() => {
		const element = ref.current;
		if (!element) return null;
		const rect = element.getBoundingClientRect();
		// Total distance the sticky child travels: the section's height minus the child's own. Measured
		// off the child rather than assumed to be one viewport, because a section whose pinned content is
		// allowed to grow past the viewport (`min-h-screen`) would otherwise reach its last step early.
		const pinned = element.firstElementChild;
		const travel = rect.height - (pinned?.getBoundingClientRect().height ?? window.innerHeight);
		if (travel <= 0) return null;
		return { top: rect.top + window.scrollY, travel };
	}, []);

	useEffect(() => {
		if (!ref.current || !enabled) return;

		let frame = 0;
		const measure = () => {
			frame = 0;
			const box = geometry();
			if (!box) return;
			setActive(stepFromOffset(window.scrollY - box.top, box.travel, steps));
		};

		const onScroll = () => {
			if (frame) return;
			frame = requestAnimationFrame(measure);
		};

		measure();
		window.addEventListener('scroll', onScroll, { passive: true });
		window.addEventListener('resize', onScroll);
		return () => {
			if (frame) cancelAnimationFrame(frame);
			window.removeEventListener('scroll', onScroll);
			window.removeEventListener('resize', onScroll);
		};
	}, [steps, enabled, geometry]);

	/**
	 * Show a step on demand — the rail's ordinals and a click on the deck both come through here.
	 *
	 * It MOVES THE PAGE rather than setting the index, so the scroll position stays the single source of
	 * truth and the deck, the rail and the scrollbar cannot disagree. Setting `active` directly would
	 * show the step for exactly as long as it took the next scroll event to overwrite it.
	 *
	 * Reduced motion is the one case that must set it, and the reason is the same read-back: there is no
	 * pin and no travel, so no scroll will ever produce an index. Without this branch a visitor with the
	 * setting on is left on the first card with no way to reach the rest — which is three quarters of
	 * the section, and the part that explains how to join.
	 */
	const goTo = useCallback(
		(index: number) => {
			if (!enabled) {
				setActive(Math.min(steps - 1, Math.max(0, index)));
				return;
			}
			const box = geometry();
			if (!box) return;
			window.scrollTo({ top: box.top + offsetForStep(index, box.travel, steps), behavior: 'smooth' });
		},
		[enabled, geometry, steps],
	);

	return { ref, active, enabled, goTo };
}
